import { civilDateOf, formatCivilDate, formatInstant } from '@/shared/format/date';
import { formatDocument, type DocumentKind } from '@/shared/format/document';
import { formatCents, formatMoney, isDecimalString } from '@/shared/format/money';
import { formatPercent } from '@/shared/format/percent';

/** Formatos de valor escalar, os mesmos nomes dos presets de coluna e do `KpiCard`. */
export type ScalarFormat = 'text' | 'money' | 'document' | 'date' | 'datetime' | 'percent' | 'number';

export interface ScalarOptions {
  /** Dinheiro: `cents` (inteiro, o fio do legado, padrão) ou `decimal` (string decimal). */
  readonly unit?: 'cents' | 'decimal';
  /** Documento: `auto` pelo tamanho (padrão), ou força CPF/CNPJ. */
  readonly document?: DocumentKind | 'auto';
  /** Percentual e número: casas máximas. */
  readonly maxFractionDigits?: number;
}

/** Vazio para a tela: `null`, `undefined`, texto em branco. Zero não é vazio. */
export function isBlank(value: unknown): value is null | undefined | '' {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

function money(value: unknown, options: ScalarOptions, locale: string): string | null {
  if (options.unit === 'decimal') {
    return typeof value === 'string' && isDecimalString(value) ? formatMoney(value, { locale }) : null;
  }
  if (typeof value === 'number' && Number.isSafeInteger(value)) return formatCents(value, { locale });
  if (typeof value === 'string' && /^-?[0-9]+$/.test(value)) return formatCents(value, { locale });
  return null;
}

function date(value: unknown, locale: string, time: boolean): string | null {
  if (typeof value !== 'string') return null;
  // Data civil (`2026-08-14`) nunca vira instante (U14); instante sai em São Paulo.
  if (!time && civilDateOf(value) === value.trim()) return formatCivilDate(value, { locale });
  return formatInstant(value, { locale, time });
}

function percent(value: unknown, options: ScalarOptions, locale: string): string | null {
  const text = typeof value === 'number' ? String(value) : value;
  if (typeof text !== 'string' || !/^[+-]?[0-9]+(\.[0-9]+)?$/.test(text.trim())) return null;
  return formatPercent(text, { locale, ...(options.maxFractionDigits === undefined ? {} : { maximumFractionDigits: options.maxFractionDigits }) });
}

function number(value: unknown, options: ScalarOptions, locale: string): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: options.maxFractionDigits ?? 2 }).format(value);
}

/**
 * Formata um valor escalar para exibição (borda da tela, contrato de formatos). `null` = vazio ou
 * fora do formato: quem desenha mostra "N/D" ou o valor cru, nunca um texto plausível inventado.
 */
export function formatScalar(format: ScalarFormat, value: unknown, locale: string, options: ScalarOptions = {}): string | null {
  if (isBlank(value)) return null;
  switch (format) {
    case 'money':
      return money(value, options, locale);
    case 'document':
      return typeof value === 'string' ? formatDocument(value, options.document ?? 'auto') : null;
    case 'date':
      return date(value, locale, false);
    case 'datetime':
      return date(value, locale, true);
    case 'percent':
      return percent(value, options, locale);
    case 'number':
      return number(value, options, locale);
    case 'text':
      return typeof value === 'string' || typeof value === 'number' ? String(value) : null;
  }
}
